'use client';

import { cn } from '@zealot/ui/lib/utils';
import * as React from 'react';
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
} from 'react-hook-form';
import { Label } from '../label';

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue | null>(null);

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  if (!fieldContext) {
    throw new Error('useFormField must be used within <FormField>');
  }

  const fieldState = getFieldState(fieldContext.name, formState);
  const id = itemContext?.id ?? '';

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

const Form = FormProvider;

type FormItemProps = React.ComponentProps<'div'>;

function FormItem({ className, ...props }: FormItemProps) {
  const id = React.useId();
  const contextValue = React.useMemo(() => ({ id }), [id]);

  return (
    <FormItemContext.Provider value={contextValue}>
      <div data-slot="form-item" className={cn('grid gap-1.5', className)} {...props} />
    </FormItemContext.Provider>
  );
}

type FormLabelProps = React.ComponentProps<typeof Label>;

function FormLabel({ className, ...props }: FormLabelProps) {
  const { error, formItemId } = useFormField();

  return (
    <Label
      data-slot="form-label"
      className={cn(error && 'text-destructive', className)}
      htmlFor={formItemId}
      {...props}
    />
  );
}

type FormControlProps = {
  // biome-ignore lint: cloneElement needs generic ReactElement
  children: React.ReactElement<any>;
};

function FormControl({ children }: FormControlProps) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return React.cloneElement(children, {
    id: formItemId,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId,
  });
}

type FormDescriptionProps = React.ComponentProps<'p'>;

function FormDescription({ className, ...props }: FormDescriptionProps) {
  const { formDescriptionId } = useFormField();

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn('text-muted-foreground text-xs', className)}
      {...props}
    />
  );
}

type FormMessageProps = React.ComponentProps<'p'>;

function FormMessage({ className, children, ...props }: FormMessageProps) {
  const { error, formMessageId } = useFormField();
  const body = error?.message ?? children;

  if (!body) return null;

  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      role="alert"
      className={cn('font-medium text-destructive text-xs', className)}
      {...props}
    >
      {body}
    </p>
  );
}

type FormFieldSimpleProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, 'render'> & {
  label: string;
  description?: string;
  required?: boolean;
  className?: string;
  render: ControllerProps<TFieldValues, TName>['render'];
};

type FormFieldComposedProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, 'render'> & {
  label?: never;
  description?: never;
  required?: never;
  className?: never;
  render: ControllerProps<TFieldValues, TName>['render'];
};

type FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = FormFieldSimpleProps<TFieldValues, TName> | FormFieldComposedProps<TFieldValues, TName>;

// gloss
function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ label, description, required, className, render, ...props }: FormFieldProps<TFieldValues, TName>) {
  const contextValue = React.useMemo(() => ({ name: props.name }), [props.name]);

  if (label !== undefined) {
    return (
      <FormFieldContext.Provider value={contextValue}>
        <Controller
          {...props}
          render={(renderProps) => (
            <FormItem className={className}>
              <FormLabel required={required}>{label}</FormLabel>
              <FormControl>{render(renderProps)}</FormControl>
              {description && <FormDescription>{description}</FormDescription>}
              <FormMessage />
            </FormItem>
          )}
        />
      </FormFieldContext.Provider>
    );
  }

  return (
    <FormFieldContext.Provider value={contextValue}>
      <Controller {...props} render={render} />
    </FormFieldContext.Provider>
  );
}

export { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField };
